import { useEffect, useRef } from 'react';
import { DEFAULT_KEYBINDINGS, type KeyAction } from '@il-colpo/shared/settings';
import { useSettingsStore } from '../../state/settingsStore.ts';

export type Keybindings = Record<KeyAction, string>;

/** The action already using `code`, ignoring `except`. */
export function findConflict(
  bindings: Keybindings,
  code: string,
  except?: KeyAction,
): KeyAction | undefined {
  return (Object.keys(bindings) as KeyAction[]).find((a) => a !== except && bindings[a] === code);
}

export type RebindResult = { ok: true; bindings: Keybindings } | { ok: false; conflict: KeyAction };

/** Binds `code` to `action`. Refuses when another action already uses it. */
export function rebind(bindings: Keybindings, action: KeyAction, code: string): RebindResult {
  const conflict = findConflict(bindings, code, action);
  if (conflict) return { ok: false, conflict };
  return { ok: true, bindings: { ...bindings, [action]: code } };
}

export function resetKeybindings(): Keybindings {
  return { ...DEFAULT_KEYBINDINGS };
}

/** Human-readable label for a `KeyboardEvent.code`. */
export function codeLabel(code: string): string {
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`;
  if (code.startsWith('Arrow')) return code.slice(5);
  return code;
}

const TYPING = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/** Calls `handler` when the key bound to `action` is pressed (not while typing in a field). */
export function useKeyAction(action: KeyAction, handler: () => void, enabled = true): void {
  const code = useSettingsStore((s) => s.settings.controls.keybindings[action]);
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== code || e.repeat) return;
      const target = e.target;
      if (
        target instanceof HTMLElement &&
        (TYPING.has(target.tagName) || target.isContentEditable)
      ) {
        return;
      }
      handlerRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [code, enabled]);
}

export type KeyHandlers = Partial<Record<KeyAction, () => void>>;

/**
 * One listener for a set of actions: runs the handler of whichever bound key is pressed.
 * Ignores keys typed into a field, held keys, shortcuts with a modifier, and everything while a
 * dialog is open (focus belongs to the dialog then). Tab is only taken when nothing has focus,
 * so that it still moves through the page for keyboard users.
 */
export function useKeyActions(handlers: KeyHandlers, enabled = true): void {
  const bindings = useSettingsStore((s) => s.settings.controls.keybindings);
  const ref = useRef(handlers);
  useEffect(() => {
    ref.current = handlers;
  });
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat || e.ctrlKey || e.altKey || e.metaKey) return;
      const target = e.target;
      if (
        target instanceof HTMLElement &&
        (TYPING.has(target.tagName) || target.isContentEditable)
      ) {
        return;
      }
      if (document.querySelector('[role="dialog"]')) return;
      const action = (Object.keys(bindings) as KeyAction[]).find((a) => bindings[a] === e.code);
      const handler = action ? ref.current[action] : undefined;
      if (!handler) return;
      if (e.code === 'Tab') {
        const at = document.activeElement;
        if (e.shiftKey || (at !== document.body && at?.id !== 'main')) return;
        e.preventDefault();
      }
      handler();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [bindings, enabled]);
}
